/**
 * PlaceholderLogo — purpose-built domain glyph shown in the site header
 * when the tenant hasn't uploaded a real logo yet (Settings → LOGO_URL).
 *
 * Intentional-looking default: a rounded navy tile with an inset globe +
 * pulsing cyan pin, followed by the tenant's brand name wordmark. Reads as
 * "domains · curated · live" without requiring any theming per tenant.
 */
export default function PlaceholderLogo({ brandName = "CasperDomains", size = 32 }) {
  return (
    <span
      className="cd-placeholder-logo"
      data-testid="site-header-placeholder-logo"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        fontFamily: "var(--cd-font)",
        fontWeight: 800,
        fontSize: 18,
        letterSpacing: "-0.01em",
        color: "var(--cd-navy-950, #03142f)",
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 40 40"
        role="img"
        aria-label={`${brandName} logo`}
        style={{ display: "block", flexShrink: 0 }}
      >
        <defs>
          <linearGradient id="cd-plh-bg" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#03142f" />
            <stop offset="65%" stopColor="#0a2456" />
            <stop offset="100%" stopColor="#0f3a8a" />
          </linearGradient>
          <linearGradient id="cd-plh-ring" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3ee8d3" />
            <stop offset="100%" stopColor="#4b6fff" />
          </linearGradient>
          <radialGradient id="cd-plh-dot" cx="50%" cy="40%" r="50%">
            <stop offset="0%" stopColor="#7cf5e2" />
            <stop offset="100%" stopColor="#3ee8d3" />
          </radialGradient>
        </defs>

        {/* Rounded tile */}
        <rect x="1" y="1" width="38" height="38" rx="10" fill="url(#cd-plh-bg)" />

        {/* Globe: circle + one meridian + one equator arc */}
        <g stroke="url(#cd-plh-ring)" strokeWidth="1.6" fill="none" strokeLinecap="round">
          <circle cx="20" cy="20" r="10.5" />
          {/* meridian */}
          <ellipse cx="20" cy="20" rx="5" ry="10.5" />
          {/* equator */}
          <path d="M9.5 20 L30.5 20" />
        </g>

        {/* Cyan live pin — top-right corner, punctuates the "live" idea */}
        <circle cx="30.5" cy="10.5" r="3.2" fill="url(#cd-plh-dot)">
          <animate attributeName="r" values="3.2;3.9;3.2" dur="2.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="1;.7;1" dur="2.4s" repeatCount="indefinite" />
        </circle>
        <circle cx="30.5" cy="10.5" r="1.4" fill="#fff" />
      </svg>
      <span>{brandName}</span>
    </span>
  );
}
